import React, {useCallback} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {RECENTLY_USED_FILTERS_LIMIT} from 'components/Filters/constants'
import {filterMessages} from 'components/Filters/filterMessages'
import {getAllFilterKeys} from 'components/Filters/utils'
import {useFiltersForm} from 'hooks/useFiltersForm'
import {useRecentlyUsedFilters} from 'hooks/useRecentlyUsedFilters'
import {getNeighborhoods, getPropertyBrands} from 'modules/sapiSearch/selectors'
import {getSearchDesktopHeaderHeight} from 'modules/search/selectors'

import {Icon} from '@vio/atlas/Icon'
import {linariaMq} from '@vio/atlas/src/utils/breakpoints'
import {cssTheme} from '@vio/atlas/themes'

import {AllFiltersContent} from '../AllFiltersContent'
import {FilterDisplayContainer} from '../FilterDisplayContainer/FilterDisplayContainer'
import {useSectionTracker} from '../SectionTracker'
import {AllFilterCategoryList} from './CategoryList'

const RIGHT_CONTENT_CONTAINER_WIDTH = 500
const RIGHT_CONTENT_CONTAINER_MAX_WIDTH = 677
// heights Popover TitleBar and BottomBar & the space under popover
const POPOVER_BAR_HEIGHT = 154

const Wrapper = styled.div<{headerHeight: number}>`
  max-height: calc(
    100vh - ${({headerHeight}) => headerHeight + POPOVER_BAR_HEIGHT}px
  );
  display: flex;
  align-items: stretch;
`

const ScrollWrapper = styled.div`
  overflow-y: scroll;
  overflow-x: hidden;
  position: relative;
  scroll-snap-type: y mandatory;
  scroll-snap-align: center;
  :first-of-type {
    border-right: ${cssTheme.colors.border.neutral.secondary}
      ${cssTheme.layout.borderWidth.bw100} solid;
  }
  overscroll-behavior: none;
  -webkit-overscroll-behavior: none;

  &.--right-content-container {
    width: ${RIGHT_CONTENT_CONTAINER_WIDTH}px;
  }
  ${linariaMq.desktopMd} {
    &.--right-content-container {
      width: ${RIGHT_CONTENT_CONTAINER_MAX_WIDTH}px;
    }
  }
`

const allFilterKeys = getAllFilterKeys()
const elementName = 'allFilters'

/**
 * Overlay which displays all filters
 */
export const AllFiltersPopoverContent = () => {
  const {formatMessage} = useIntl()
  const {
    getAppliedFiltersCount,
    getAppliedStarsLabel,
    getFilters,
    getAppliedFilters,
    toggleFilters,
    toggleMultiFilter,
    setMultiFilters,
    removeMultiFilters,
    submitFilters,
    clearFilters,
    resetFilters,
    trackFilterClick,
    getFiltersCount
  } = useFiltersForm(allFilterKeys, elementName)

  const appliedFiltersCount = getAppliedFiltersCount()
  const filtersCount = getFiltersCount()

  const neighborhoods = useSelector(getNeighborhoods)
  const propertyBrands = useSelector(getPropertyBrands)

  const {getMappedAllRecentlyUsedFilters} = useRecentlyUsedFilters()
  const recentlyUsedFilters = getMappedAllRecentlyUsedFilters({
    limit: RECENTLY_USED_FILTERS_LIMIT,
    neighborhoods,
    propertyBrands
  })

  const handleApply = useCallback(() => {
    submitFilters()
  }, [submitFilters])

  const {rightContentContainerRef} = useSectionTracker()
  const headerHeight = useSelector(getSearchDesktopHeaderHeight)

  return (
    <FilterDisplayContainer
      dataId="allFiltersFilter"
      label={formatMessage(filterMessages.allFilters)}
      title={formatMessage(filterMessages.allFilters)}
      icon={<Icon name="SettingsSliders" />}
      appliedFiltersCount={appliedFiltersCount}
      filtersCount={filtersCount}
      onClear={clearFilters}
      onApply={handleApply}
      onClose={resetFilters}
      onPillClick={trackFilterClick}
    >
      <Wrapper headerHeight={headerHeight}>
        <ScrollWrapper data-body-scroll-lock-ignore>
          <AllFilterCategoryList
            hasRecentlyUsedFilters={recentlyUsedFilters.length > 0}
          />
        </ScrollWrapper>
        <ScrollWrapper
          data-body-scroll-lock-ignore
          ref={rightContentContainerRef}
          className="--right-content-container"
        >
          <AllFiltersContent
            getFilters={getFilters}
            getAppliedFilters={getAppliedFilters}
            toggleFilters={toggleFilters}
            toggleMultiFilter={toggleMultiFilter}
            getFiltersCount={getFiltersCount}
            setMultiFilters={setMultiFilters}
            removeMultiFilters={removeMultiFilters}
            getAppliedFiltersCount={getAppliedFiltersCount}
            getAppliedStarsLabel={getAppliedStarsLabel}
            recentlyUsedFilters={recentlyUsedFilters}
          />
        </ScrollWrapper>
      </Wrapper>
    </FilterDisplayContainer>
  )
}
